import { IonRow, IonSelectOption, IonSpinner } from '@ionic/react';
import { t } from '@lingui/macro';
import QrScanner from 'qr-scanner';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import DefaultSelect from '../forms/defaultselect/DefaultSelect';

type QRCodeScannerProps = {
    onScanSuccess: (decodedText: string) => void;
    onScanError?: (errorMessage: string) => void;
};
const QRCodeScanner: React.FC<QRCodeScannerProps> = memo((props) => {
    const videoEl = useRef<HTMLVideoElement | null>(null);
    const qrScanner = useRef<QrScanner | null>(null);
    const timeout = useRef<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [cameras, setCameras] = useState<QrScanner.Camera[] | null>(null);
    const [selectedCamera, setSelectedCamera] =
        useState<QrScanner.Camera | null>(null);

    const refreshScanRegion = useCallback(() => {
        timeout.current = window.setTimeout(() => {
            // For some reason the scan region is not calculated correctly
            // when starting camera or changing camera.
            // The plugin does not have a refresh method but looking into the
            // source code, the scan region is calculated on the loadedmetadata
            // event. See:
            // - https://github.com/nimiq/qr-scanner/blob/master/src/qr-scanner.ts#L262
            // - https://github.com/nimiq/qr-scanner/blob/master/src/qr-scanner.ts#L668
            // To workaround this issue, we dispatch the loadedmetadata event
            videoEl.current?.dispatchEvent(new Event('loadedmetadata'));
        }, 500);
    }, []);

    const onCameraChange = useCallback(
        async (event: CustomEvent) => {
            setLoading(true);
            try {
                const [selectedCameraId, selectedCameraLabel] =
                    event.detail.value.split('-tiko-');
                const camera =
                    cameras?.find(
                        (camera) =>
                            camera.id === selectedCameraId &&
                            camera.label === selectedCameraLabel
                    ) || null;
                if (!camera) {
                    throw Error('Invalid camera selected');
                }
                await qrScanner.current?.setCamera(camera.id);
                setSelectedCamera(camera);
            } catch (error) {
                console.log('QRCodeScanner:onCameraChange:error', error);
            } finally {
                setLoading(false);
                refreshScanRegion();
            }
        },
        [cameras, refreshScanRegion]
    );

    const getCameras = useCallback(async () => {
        try {
            const cameras = await QrScanner.listCameras(true);
            if (cameras.length === 0) {
                throw Error('No camera found');
            }
            // For Android there are several cameras but the one that works
            // better is the one with label 'camera2 0, facing back' so we need
            // to filter it out and apply this camera to the default if it exists
            const bestAndroidCamera = cameras.find((camera) =>
                camera.label.includes('camera2 0, facing back')
            );
            const defaultCamera = {
                id: bestAndroidCamera ? bestAndroidCamera.id : 'environment',
                label: t`Default Camera`,
            };
            setCameras([defaultCamera, ...cameras]);
            await qrScanner.current?.setCamera(defaultCamera.id);
            setSelectedCamera(defaultCamera);
        } catch (error) {
            console.log('QRCodeScanner:getCameras:error', error);
        }
    }, []);

    const buildScanner = useCallback(() => {
        qrScanner.current = new QrScanner(
            videoEl.current!,
            (result) => props.onScanSuccess(result.data),
            {
                preferredCamera: 'environment',
                maxScansPerSecond: 1,
                highlightScanRegion: true,
                highlightCodeOutline: true,
                returnDetailedScanResult: true,
                calculateScanRegion: (v) => {
                    const smallestDimension = Math.min(
                        v.videoWidth,
                        v.videoHeight
                    );

                    // Make scan region smaller to match better small qr codes
                    const scanRegionSize = Math.round(
                        (1 / 4) * smallestDimension
                    );

                    const region: QrScanner.ScanRegion = {
                        x: Math.round((v.videoWidth - scanRegionSize) / 2),
                        y: Math.round((v.videoHeight - scanRegionSize) / 2),
                        width: scanRegionSize,
                        height: scanRegionSize,
                    };
                    return region;
                },
            }
        );
    }, [props]);

    const stopScanner = useCallback(async () => {
        await qrScanner.current?.stop();
    }, []);

    const destroyScanner = useCallback(() => {
        qrScanner.current?.destroy();
        qrScanner.current = null;
    }, []);

    const startScanner = useCallback(async () => {
        await qrScanner.current?.start();
    }, []);

    const mountScanner = useCallback(async () => {
        await buildScanner();
        await getCameras();
        await startScanner();
        setLoading(false);
        refreshScanRegion();
    }, [buildScanner, getCameras, refreshScanRegion, startScanner]);

    const unmountScanner = useCallback(async () => {
        await stopScanner();
        await destroyScanner();
    }, [destroyScanner, stopScanner]);

    useEffect(() => {
        mountScanner();

        return () => {
            timeout.current && clearTimeout(timeout.current);
            unmountScanner();
        };
    }, [mountScanner, unmountScanner]);

    return (
        <>
            <video
                ref={videoEl}
                style={{
                    // width: '100%',
                    // height: loading ? '0' : 'auto',
                    width: '100vw',
                    height: loading ? '0' : '60vh',
                    objectFit: 'cover',
                }}
            ></video>
            {loading ? (
                <IonRow className="ion-padding ion-justify-content-center">
                    <IonSpinner name="circular"></IonSpinner>
                </IonRow>
            ) : cameras ? (
                cameras?.length > 1 && (
                    <div className="ion-padding">
                        {cameras && (
                            <DefaultSelect
                                label={t`Select camera`}
                                value={`${selectedCamera?.id}-tiko-${selectedCamera?.label}`}
                                placeholder={t`Select a camera`}
                                onIonChange={onCameraChange}
                            >
                                {cameras.map((camera) => (
                                    <IonSelectOption
                                        key={`${camera.id}-tiko-${camera.label}`}
                                        value={`${camera.id}-tiko-${camera.label}`}
                                    >
                                        {camera.label}
                                    </IonSelectOption>
                                ))}
                            </DefaultSelect>
                        )}
                    </div>
                )
            ) : (
                <div className="ion-padding">No camera found</div>
            )}
        </>
    );
});

export default QRCodeScanner;
